// Home page — v2.2.
// Highlights: Command-palette search (Ctrl/⌘+K), 3-tab layout
// (Medical Algorithm · Drug & Fluid · Clinical Scores).

import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Droplet,
  Pill,
  Activity,
  FlaskConical,
  Brain,
  ListChecks,
  Scale,
  Baby,
  Siren,
  Beaker,
  Flame,
  ArrowUpRight,
  Heart,
  Wind,
  Zap,
  Layers,
  Search,
  Command,
  ClipboardList,
  ThermometerSun,
  BookOpen,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";
import { SPECIALTIES, specialtyCounts } from "@/lib/algorithms";
import { MEDICATIONS } from "@/lib/medications";
import { EMERGENCY_DRUGS } from "@/lib/emergency_drugs";

const DRUG_CARDS = [
  { to: "/iv-fluid", labelKey: "nav.iv_fluid", descKey: "home.card.iv_fluid.desc", icon: Droplet, testid: "card-iv-fluid" },
  { to: "/pediatric-dose", labelKey: "ped.title", descKey: "home.card.pediatric.desc", icon: Pill, testid: "card-pediatric-dose" },
  { to: "/emergency-drugs", labelKey: "emerg.title", descKey: "emerg.subtitle", icon: Siren, testid: "card-emergency" },
  { to: "/heparin", labelKey: "hep.title", descKey: "hep.subtitle", icon: Beaker, testid: "card-heparin" },
  { to: "/baxter", labelKey: "baxter.title", descKey: "baxter.subtitle", icon: Flame, testid: "card-baxter" },
  { to: "/drip-rate", labelKey: "nav.drip", descKey: "home.card.drip.desc", icon: Activity, testid: "card-drip-rate" },
];

const SCORE_CARDS = [
  { to: "/nihss", labelKey: "nav.nihss", descKey: "home.card.nihss.desc", icon: Brain, testid: "card-nihss" },
  { to: "/alvarado", labelKey: "nav.alvarado", descKey: "home.card.alvarado.desc", icon: ListChecks, testid: "card-alvarado" },
  { to: "/bmi", labelKey: "bmi.title", descKey: "bmi.subtitle", icon: Scale, testid: "card-bmi" },
  { to: "/killip", labelKey: "score.killip.title", descKey: "score.killip.desc", icon: Heart, testid: "card-killip" },
  { to: "/sofa", labelKey: "score.sofa.title", descKey: "score.sofa.desc", icon: Activity, testid: "card-sofa" },
  { to: "/news", labelKey: "score.news.title", descKey: "score.news.desc", icon: ThermometerSun, testid: "card-news" },
  { to: "/apgar", labelKey: "score.apgar.title", descKey: "score.apgar.desc", icon: Baby, testid: "card-apgar" },
  { to: "/ballard", labelKey: "score.ballard.title", descKey: "score.ballard.desc", icon: Baby, testid: "card-ballard" },
  { to: "/downe", labelKey: "score.downe.title", descKey: "score.downe.desc", icon: Wind, testid: "card-downe" },
];

const SPEC_META = {
  cardiovascular: { icon: Heart, accent: "text-teal-700 border-teal-700/40 bg-teal-700/5 hover:border-teal-700 hover:shadow-sm" },
  respiratory: { icon: Wind, accent: "text-sky-700 border-sky-700/40 bg-sky-700/5 hover:border-sky-700 hover:shadow-sm" },
  neurology: { icon: Zap, accent: "text-purple-700 border-purple-700/40 bg-purple-700/5 hover:border-purple-700 hover:shadow-sm" },
  pediatric: { icon: Baby, accent: "text-amber-700 border-amber-700/40 bg-amber-700/5 hover:border-amber-700 hover:shadow-sm" },
  anestesi: { icon: Layers, accent: "text-stone-700 border-stone-300 bg-stone-100 hover:border-stone-500 hover:shadow-sm" },
};

const TABS = [
  { key: "algorithm", labelKey: "tabs.algorithm", icon: BookOpen },
  { key: "drug", labelKey: "tabs.drug", icon: Beaker },
  { key: "scores", labelKey: "tabs.scores", icon: ClipboardList },
];

// -- Search modal ----------------------------------------------------------

function SearchModal({ isOpen, onClose, t, lang }) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [isOpen]);

  const groupLabels = {
    algo: lang === "id" ? "Algoritma Spesifik" : "Specific Algorithms",
    med_ped: lang === "id" ? "Obat Pediatrik" : "Pediatric Medications",
    med_emerg: lang === "id" ? "Obat Darurat / ICU" : "Emergency / ICU Drugs",
    calc: lang === "id" ? "Kalkulator Obat & Cairan" : "Drug & Fluid Calculators",
    score: lang === "id" ? "Skor Klinis" : "Clinical Scores",
    spec: lang === "id" ? "Kategori Spesialisasi" : "Specialty Categories",
  };

  const searchIndex = useMemo(() => {
    const items = [];

    Object.keys(SPECIALTIES).forEach((key) => {
      const spec = SPECIALTIES[key];
      const specName = spec.name[lang] || spec.name.en;
      const counts = specialtyCounts(key);
      items.push({
        id: `spec-${key}`,
        title: specName,
        desc: (lang === "id" ? "Kategori · " : "Category · ") + `${counts.available}/${counts.total}`,
        group: "spec",
        to: `/algorithm/${key}`,
        available: counts.available > 0,
      });
      spec.order.forEach((algoId) => {
        const algo = spec.algorithms[algoId];
        if (!algo) return;
        const title = algo.title[lang] || algo.title.en;
        const descStr = algo.desc ? algo.desc[lang] || algo.desc.en || "" : "";
        items.push({
          id: `algo-${key}-${algoId}`,
          title,
          desc: descStr ? `${specName} · ${descStr}` : specName,
          group: "algo",
          to: `/algorithm/${key}/${algoId}`,
          available: algo.available,
        });
      });
    });

    DRUG_CARDS.forEach((c) => {
      items.push({
        id: c.testid,
        title: t(c.labelKey),
        desc: t(c.descKey),
        group: "calc",
        to: c.to,
        available: true,
      });
    });

    SCORE_CARDS.forEach((c) => {
      items.push({
        id: c.testid,
        title: t(c.labelKey),
        desc: t(c.descKey),
        group: "score",
        to: c.to,
        available: true,
      });
    });

    // Deep-linkable pediatric medications.
    MEDICATIONS.forEach((med) => {
      items.push({
        id: `med-ped-${med.id}`,
        title: med.name,
        desc: lang === "id" ? "Kalkulator Dosis Anak" : "Pediatric Dose Calculator",
        group: "med_ped",
        to: `/pediatric-dose?drug=${med.id}`,
        available: true,
      });
    });

    // Deep-linkable emergency / ICU infusions.
    EMERGENCY_DRUGS.forEach((drug) => {
      items.push({
        id: `med-emerg-${drug.id}`,
        title: drug.name,
        desc: lang === "id" ? "Kalkulator Infus / Syringe Pump" : "Infusion / Syringe Pump Calculator",
        group: "med_emerg",
        to: `/emergency-drugs?drug=${drug.id}`,
        available: true,
      });
    });

    return items;
  }, [t, lang]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return searchIndex.filter((it) => it.title?.toLowerCase().includes(q) || it.desc?.toLowerCase().includes(q));
  }, [query, searchIndex]);

  const groupOrder = ["algo", "med_ped", "med_emerg", "score", "calc", "spec"];
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((it) => {
      if (!map[it.group]) map[it.group] = [];
      map[it.group].push(it);
    });
    return map;
  }, [filtered]);

  const flat = useMemo(() => groupOrder.flatMap((g) => grouped[g] || []), [grouped]);

  useEffect(() => {
    const onKey = (e) => {
      if (!isOpen) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(flat.length - 1, i + 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      }
      if (e.key === "Enter" && flat[activeIdx] && flat[activeIdx].available) {
        e.preventDefault();
        navigate(flat[activeIdx].to);
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, flat, activeIdx, navigate, onClose]);

  if (!isOpen) return null;

  const isID = lang === "id";

  return (
    <div
      className="fixed inset-0 z-[70] bg-stone-900/50 backdrop-blur-sm flex justify-center items-start pt-[10vh] transition-opacity px-4"
      onClick={onClose}
      data-testid="search-modal"
    >
      <div
        className="bg-white w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden border border-stone-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center px-5 py-4 border-b border-stone-200">
          <Search size={20} className="text-stone-400 mr-3 shrink-0" />
          <input
            ref={inputRef}
            data-testid="search-input"
            type="text"
            className="flex-1 bg-transparent text-base md:text-lg text-stone-900 placeholder:text-stone-400 outline-none"
            placeholder={
              isID
                ? "Cari algoritma, kalkulator, atau skor…"
                : "Search algorithms, calculators, or scores…"
            }
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
          />
          <button
            onClick={onClose}
            className="text-[10px] font-mono bg-stone-100 text-stone-500 px-2 py-1 rounded border border-stone-200 hover:bg-stone-200 transition-colors shrink-0"
          >
            ESC
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto p-2">
          {query.trim() === "" ? (
            <SearchHints isID={isID} />
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-stone-500">
              {isID ? "Tidak ada hasil untuk" : "No results for"}{" "}
              <span className="font-mono text-stone-800">"{query}"</span>
            </div>
          ) : (
            <>
              {groupOrder.map((g) => {
                const rows = grouped[g];
                if (!rows || rows.length === 0) return null;
                return (
                  <div key={g} className="mb-2">
                    <div className="px-3 py-2 text-[10px] font-bold tracking-widest uppercase text-stone-400">
                      {groupLabels[g]}
                    </div>
                    {rows.map((item) => {
                      const idx = flat.findIndex((f) => f.id === item.id);
                      const isActive = idx === activeIdx;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onMouseEnter={() => setActiveIdx(idx)}
                          onClick={() => {
                            if (item.available) {
                              navigate(item.to);
                              onClose();
                            }
                          }}
                          data-testid={`search-result-${item.id}`}
                          className={cn(
                            "w-full text-left px-3 py-2.5 rounded-md flex items-center justify-between gap-3 transition-colors",
                            !item.available && "opacity-60 cursor-not-allowed",
                            item.available && (isActive ? "bg-sky-50" : "hover:bg-stone-50")
                          )}
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-stone-900 truncate">{item.title}</div>
                            <div className="text-xs text-stone-500 truncate">{item.desc}</div>
                          </div>
                          {!item.available && (
                            <span className="text-[9px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded uppercase tracking-wider font-semibold whitespace-nowrap">
                              {isID ? "Segera Hadir" : "Coming Soon"}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-between text-[10px] font-mono text-stone-500">
          <div className="flex items-center gap-3">
            <span>
              <kbd className="bg-white border border-stone-300 rounded px-1.5 py-0.5">↑</kbd>{" "}
              <kbd className="bg-white border border-stone-300 rounded px-1.5 py-0.5">↓</kbd> {isID ? "pilih" : "select"}
            </span>
            <span>
              <kbd className="bg-white border border-stone-300 rounded px-1.5 py-0.5">↵</kbd> {isID ? "buka" : "open"}
            </span>
          </div>
          <span>{filtered.length > 0 && `${filtered.length} ${isID ? "hasil" : "results"}`}</span>
        </div>
      </div>
    </div>
  );
}

function SearchHints({ isID }) {
  const suggestions = isID
    ? ["stemi", "sepsis", "asma", "heparin", "killip"]
    : ["stemi", "sepsis", "asthma", "heparin", "killip"];
  return (
    <div className="p-4">
      <div className="text-xs text-stone-500 mb-3">{isID ? "Coba cari:" : "Try searching:"}</div>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((s) => (
          <span key={s} className="font-mono text-xs bg-stone-100 border border-stone-200 rounded px-2 py-1 text-stone-700">
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

// -- Home ------------------------------------------------------------------

export default function Home() {
  const { t, lang } = useLanguage();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div data-testid="home-page">
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} t={t} lang={lang} />

      <section className="px-6 md:px-10 pt-12 md:pt-20 pb-4 border-b border-stone-200 bg-white">
        <div className="max-w-3xl">
          <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-sky-700 mb-3">
            {t("home.kicker")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-stone-900 leading-[1.05]">
            {t("home.headline.1")}
            <br />
            <span className="text-sky-700">{t("home.headline.2")}</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-stone-600 leading-relaxed max-w-2xl">
            {t("home.lead")}
          </p>
          <p data-testid="home-collab" className="mt-5 mb-2 text-sm md:text-base text-stone-600 leading-relaxed max-w-2xl">
            {t("home.collab.prefix")}
            <a
              href="https://www.instagram.com/soresendiribelajar/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-collab-handle"
              className="font-semibold text-sky-700 hover:text-sky-800 no-underline"
            >
              @soresendiribelajar
            </a>
            {t("home.collab.suffix")}
          </p>
          <p data-testid="home-collab-mednotecard" className="mb-2 text-sm md:text-base text-stone-600 leading-relaxed max-w-3xl">
            {t("home.collab2.prefix")}
            <a
              href="https://www.instagram.com/mednotecard/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-collab-mednotecard-handle"
              className="font-semibold text-sky-700 hover:text-sky-800 no-underline"
            >
              @mednotecard
            </a>
            {t("home.collab2.suffix")}
          </p>

          {/* Prominent search trigger */}
          <button
            type="button"
            onClick={() => setIsSearchOpen(true)}
            data-testid="search-trigger"
            className="mt-8 w-full max-w-xl flex items-center gap-3 bg-white border-2 border-stone-200 hover:border-sky-700 focus:border-sky-700 focus:outline-none rounded-lg px-4 py-3.5 min-h-[56px] shadow-sm hover:shadow transition-all group"
          >
            <Search size={20} strokeWidth={1.75} className="text-stone-400 group-hover:text-sky-700 transition-colors shrink-0" />
            <span className="flex-1 text-left text-sm md:text-base text-stone-500 group-hover:text-stone-800 transition-colors">
              {lang === "id" ? "Cari algoritma, kalkulator, atau skor…" : "Search algorithms, calculators, or scores…"}
            </span>
            <span className="hidden sm:inline-flex items-center gap-1 font-mono text-[10px] font-semibold text-stone-400 shrink-0">
              <kbd className="bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5 shadow-sm inline-flex items-center gap-0.5">
                <Command size={10} strokeWidth={2.5} />K
              </kbd>
            </span>
          </button>
        </div>
      </section>

      <section className="px-6 md:px-10 pt-2 pb-8 md:pt-4 md:pb-10">
        <HomeTabs t={t} />
      </section>
    </div>
  );
}

// -- Tabs ------------------------------------------------------------------

function HomeTabs({ t }) {
  const [active, setActive] = useState("algorithm");
  return (
    <>
      <div
        role="tablist"
        className="flex flex-wrap gap-1 border-b border-stone-200 mb-8 -mx-6 md:-mx-10 px-6 md:px-10"
        data-testid="home-tabs"
      >
        {TABS.map((tab) => {
          const isActive = active === tab.key;
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`tab-${tab.key}`}
              onClick={() => setActive(tab.key)}
              className={cn(
                "px-4 py-3 min-h-[44px] text-sm font-medium border-b-2 transition-colors duration-100 flex items-center gap-2",
                isActive
                  ? "border-sky-700 text-sky-700"
                  : "border-transparent text-stone-500 hover:text-stone-900"
              )}
            >
              <Icon size={16} strokeWidth={1.75} />
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {active === "algorithm" && <AlgorithmTab t={t} />}
      {active === "drug" && <ShortcutsTab t={t} cards={DRUG_CARDS} panelId="drug" introKey="drug.intro" titleKey="tabs.drug" />}
      {active === "scores" && <ShortcutsTab t={t} cards={SCORE_CARDS} panelId="scores" introKey="scores.intro" titleKey="tabs.scores" />}
    </>
  );
}

function AlgorithmTab({ t }) {
  const { lang } = useLanguage();
  const specKeys = Object.keys(SPECIALTIES);
  return (
    <div data-testid="tab-panel-algorithm">
      <div className="max-w-3xl mb-6">
        <h2 className="font-display text-2xl tracking-tight font-medium text-stone-900">{t("tabs.algorithm")}</h2>
        <p className="mt-2 text-sm text-stone-600 leading-relaxed">{t("algo.intro")}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {specKeys.map((key) => {
          const s = SPECIALTIES[key];
          const meta = SPEC_META[key] || SPEC_META.cardiovascular;
          const Icon = meta.icon;
          const counts = specialtyCounts(key);
          const label = s.name[lang] || s.name.en;
          const hasAvailable = counts.available > 0;
          const testid = `spec-${key}`;
          const countText = t("algo.count").replace("{n}", `${counts.available}/${counts.total}`);
          const inner = (
            <>
              <div className="flex items-start justify-between">
                <Icon size={22} strokeWidth={1.5} />
                <ArrowUpRight size={16} strokeWidth={1.5} className="opacity-50" />
              </div>
              <h3 className="mt-6 font-display text-lg tracking-tight text-stone-900">{label}</h3>
              <div className="mt-1 text-xs font-mono text-stone-500">{countText}</div>
              {!hasAvailable && (
                <div className="mt-3 text-[10px] tracking-[0.14em] uppercase font-mono text-stone-500">
                  {t("common.coming_v22")}
                </div>
              )}
            </>
          );
          if (hasAvailable) {
            return (
              <Link
                key={key}
                to={`/algorithm/${key}`}
                data-testid={testid}
                className={cn("text-left border rounded-sm p-5 transition-all duration-100 bg-white block", meta.accent)}
              >
                {inner}
              </Link>
            );
          }
          return (
            <div
              key={key}
              data-testid={testid}
              className={cn("text-left border rounded-sm p-5 bg-white opacity-70 cursor-not-allowed", meta.accent)}
              aria-disabled="true"
            >
              {inner}
            </div>
          );
        })}
      </div>
      <p className="mt-8 text-xs text-stone-500 max-w-xl leading-relaxed" data-testid="algorithm-soon">
        {t("algo.footer_note")}
      </p>
    </div>
  );
}

function ShortcutsTab({ t, cards, panelId, introKey, titleKey }) {
  return (
    <div data-testid={`tab-panel-${panelId}`}>
      <div className="max-w-3xl mb-6">
        <h2 className="font-display text-2xl tracking-tight font-medium text-stone-900">{t(titleKey)}</h2>
        <p className="mt-2 text-sm text-stone-600 leading-relaxed">{t(introKey)}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`${panelId}-shortcut-${c.testid}`}
              className="group border border-stone-200 bg-white p-4 hover:border-sky-700/40 hover:shadow-sm rounded-md flex items-start gap-3 transition-all min-h-[76px]"
            >
              <span className="mt-0.5 shrink-0 h-9 w-9 rounded-sm border border-sky-700/30 bg-sky-700/5 text-sky-700 flex items-center justify-center">
                <Icon size={18} strokeWidth={1.5} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-medium text-stone-900">{t(c.labelKey)}</span>
                  <ArrowUpRight size={14} className="text-stone-400 group-hover:text-sky-700 shrink-0 mt-0.5" />
                </div>
                <div className="text-xs text-stone-500 mt-0.5 line-clamp-2">{t(c.descKey)}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
